import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Typography } from '@material-ui/core';
import { useSettings } from '../../contexts/settings-context';
import { TOUR_STEPS } from './onboarding-tour-steps';

interface Rect {
    top: number;
    left: number;
    width: number;
    height: number;
}

interface OnboardingTourProps {
    open: boolean;
    /** completed=true when the user reached the last step (vs. skipping). */
    onClose: (completed?: boolean) => void;
}

const OVERLAY_Z = 1300;
const TOOLTIP_WIDTH = 320;
const GAP = 16;

export const OnboardingTour: FC<OnboardingTourProps> = ({ open, onClose }) => {
    const navigate = useNavigate();
    const { settings, saveSettings } = useSettings();
    const [index, setIndex] = useState(0);
    const [rect, setRect] = useState<Rect | null>(null);
    const [prevPin, setPrevPin] = useState<boolean | null>(null);

    const step = TOUR_STEPS[index];

    // Pin the sidebar open while the tour runs so its labels are visible to highlight.
    useEffect(() => {
        if (!open) {
            return;
        }
        setIndex(0);
        setPrevPin(settings.pinSidebar);
        if (!settings.pinSidebar) {
            saveSettings({ ...settings, pinSidebar: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Navigate to the current step's screen, then locate its sidebar item.
    useEffect(() => {
        if (!open || !step) {
            return undefined;
        }
        navigate(step.route);

        const measure = () => {
            const el = document.querySelector(step.selector);
            if (el) {
                const r = el.getBoundingClientRect();
                setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
            } else {
                // Mobile (sidebar unmounted) or not yet rendered — centered fallback.
                setRect(null);
            }
        };

        // Allow the route change + the sidebar's 250ms width transition to settle.
        const timer = setTimeout(measure, 350);
        window.addEventListener('resize', measure);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', measure);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, index]);

    if (!open || !step) {
        return null;
    }

    const close = (completed: boolean) => {
        if (prevPin === false) {
            saveSettings({ ...settings, pinSidebar: false });
        }
        onClose(completed);
    };

    const isLast = index === TOUR_STEPS.length - 1;
    const handleNext = () => (isLast ? close(true) : setIndex((i) => i + 1));
    const handleBack = () => setIndex((i) => Math.max(0, i - 1));

    // Position the tooltip beside the highlighted item, clamped to the viewport.
    const tooltipStyle = rect
        ? {
              top: Math.min(
                  Math.max(rect.top, GAP),
                  window.innerHeight - 220
              ),
              left: Math.min(
                  rect.left + rect.width + GAP,
                  window.innerWidth - TOOLTIP_WIDTH - GAP
              )
          }
        : {
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
          };

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: OVERLAY_Z, pointerEvents: 'none' }}>
            {/* Spotlight: a transparent hole over the target, dimming everything else. */}
            {rect && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: rect.top - 4,
                        left: rect.left - 4,
                        width: rect.width + 8,
                        height: rect.height + 8,
                        borderRadius: 1,
                        boxShadow: '0 0 0 9999px rgba(9, 30, 66, 0.55)',
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        pointerEvents: 'none'
                    }}
                />
            )}
            {/* Dim the whole screen when there is no target (centered fallback). */}
            {!rect && (
                <Box sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(9, 30, 66, 0.55)' }} />
            )}

            <Paper
                elevation={12}
                sx={{
                    position: 'fixed',
                    width: TOOLTIP_WIDTH,
                    p: 2,
                    borderRadius: 2,
                    pointerEvents: 'auto',
                    ...tooltipStyle
                }}
            >
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                    {step.title}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {step.body}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="caption" color="textSecondary" sx={{ flexGrow: 1 }}>
                        Step {index + 1} of {TOUR_STEPS.length}
                    </Typography>
                    <Button size="small" color="inherit" onClick={() => close(false)} sx={{ mr: 1 }}>
                        Skip tour
                    </Button>
                    {index > 0 && (
                        <Button size="small" onClick={handleBack} sx={{ mr: 1 }}>
                            Back
                        </Button>
                    )}
                    <Button size="small" variant="contained" onClick={handleNext}>
                        {isLast ? 'Finish' : 'Next'}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};
